import type { MouseEventHandler, ReactNode } from 'react';

import Link from 'components/Link';
import type { HTMLAttributes, HTMLBlockContainerTagName } from 'types';
import { cn } from 'utils/classNames';

type ListItemAlignment = 'center' | 'end' | 'start';

interface BaseProps extends HTMLAttributes<HTMLElement> {
	/** Container flex alignment. */
	alignment?: ListItemAlignment;

	/** Container tag. */
	as: HTMLBlockContainerTagName;

	/** Main content below the title. */
	children?: ReactNode;

	/** Additional container class names. */
	className?: string;

	/** Content to the right of the 'title content'. */
	endContent?: ReactNode;

	/** Give the item a thicker, green border. */
	isSelected?: boolean;

	/** Click handler, will make the title a button if there isn't a `href` set. */
	onClick?: MouseEventHandler<HTMLElement>;

	/** Content to the left of the 'title content'. */
	startContent?: ReactNode;

	/** Primary title, is the action text when button or link. */
	title: string;
}

interface DefaultProps extends BaseProps {
	href?: never;
	shouldScrollToTop?: never;
}

interface LinkProps extends BaseProps {
	/** URL, will make the title a link. */
	href: string | undefined;

	/** Disable scrolling to top for link navigation. */
	shouldScrollToTop?: boolean;
}

type Props = DefaultProps | LinkProps;

const alignmentClasses: Record<ListItemAlignment, string> = {
	start: 'items-start',
	center: 'items-center',
	end: 'items-end',
};

/**
 * A bordered box with a title.
 *
 * Not necessarily an actual list item, the name comes from the design system
 * but ListItems are sometimes used outside of lists.
 */
export default function ListItem({
	alignment,
	as: Component,
	children,
	className,
	endContent,
	href,
	isSelected,
	onClick,
	shouldScrollToTop,
	startContent,
	title,
	...attrs
}: Props) {
	const TitleTag = href ? Link : onClick ? 'button' : 'span';
	const alignClass = alignment ? alignmentClasses[alignment] : '';

	return (
		<Component
			{...attrs}
			className={cn(
				'relative flex gap-x-2 p-4 font-body text-md',
				alignClass,
				className,
			)}
		>
			{startContent}
			<div className="grow">
				<TitleTag
					// The Link prop doesn't accept undefined but TitleTag won't be a
					// link if href is undefined so it's fine here.
					href={href!}
					scroll={href ? shouldScrollToTop : undefined}
					onClick={onClick}
					type={TitleTag === 'button' ? 'button' : undefined}
					className={cn(
						'block font-bold',
						children && 'mb-1',
						// Stretch the title over the entire box with a pseudo element
						// and set the border on that.
						'before:absolute before:inset-0 before:z-1 before:rounded-xs before:border',
						!isSelected && 'before:border-strong',
						// Add an outline for the thicker border instead of increasing the
						// border width. This prevents layout shifting when selecting.
						isSelected &&
							'before:border-success before:outline-1 before:outline-success before:outline-solid',
						(href || onClick) && [
							'hover:before:outline-1 hover:before:outline-solid',
							!isSelected && 'hover:before:border-focus',
						],
						!href && !onClick && 'before:pointer-events-none',
					)}
				>
					{title}
				</TitleTag>
				{children}
			</div>
			{endContent}
		</Component>
	);
}
ListItem.displayName = 'ListItem';
